import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Slider,
} from "@heroui/react";

const PRESETS = [
  ["#7C3AED", "#0EA5E9"], // purple → sky
  ["#F59E0B", "#EF4444"], // amber → red
  ["#22C55E", "#14B8A6"], // green → teal
  ["#3B82F6", "#A855F7"], // blue → violet
  ["#F97316", "#F43F5E"], // orange → rose
  ["#FDE047", "#22C55E"], // yellow → green
];

function normalizeHex(v = "") {
  const s = v.trim();
  if (!s) return "";
  return s.startsWith("#") ? s : `#${s}`;
}

function isHexColor(v) {
  return /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/.test(v ?? "");
}

export default function BannerGradientPickerModal({
  isOpen,
  onClose,
  initial = { from: "#7C3AED", to: "#0EA5E9", angle: 45 },
  onSave, // (value: { from, to, angle }) => Promise<void> | void
}) {
  const [from, setFrom] = useState(initial.from ?? "#7C3AED");
  const [to, setTo] = useState(initial.to ?? "#0EA5E9");
  const [angle, setAngle] = useState(
    typeof initial.angle === "number" ? initial.angle : 45
  );

  useEffect(() => {
    if (!isOpen) return;
    setFrom(initial.from ?? "#7C3AED");
    setTo(initial.to ?? "#0EA5E9");
    setAngle(typeof initial.angle === "number" ? initial.angle : 45);
  }, [isOpen, initial.from, initial.to, initial.angle]);

  const previewStyle = useMemo(
    () => ({
      background: `linear-gradient(${angle}deg, ${from}, ${to})`,
      imageRendering: "pixelated",
    }),
    [from, to, angle]
  );

  const canSave = isHexColor(from) && isHexColor(to) && angle >= 0 && angle <= 360;

  const applyPreset = ([pf, pt]) => {
    setFrom(pf);
    setTo(pt);
  };

  const handleSave = async () => {
    if (!canSave) return;
    await onSave?.({ from, to, angle });
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose} placement="center" size="lg">
      <ModalContent className="bg-slate-900 text-white">
        <ModalHeader className="text-lg font-semibold">
          Edit Banner Gradient
        </ModalHeader>

        <ModalBody className="gap-6">
          {/* Live Preview */}
          <div className="rounded-xl h-28 w-full ring-1 ring-white/10 overflow-hidden" style={previewStyle} />

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  label="From"
                  value={from}
                  onValueChange={(v) => setFrom(normalizeHex(v))}
                  variant="bordered"
                  className="text-white"
                  isInvalid={!!from && !isHexColor(from)}
                  errorMessage={!isHexColor(from) ? "Enter a valid hex color" : undefined}
                />
              </div>
              <input
                aria-label="From color"
                type="color"
                value={isHexColor(from) ? from : "#7C3AED"}
                onChange={(e) => setFrom(e.target.value)}
                className="w-11 h-11 rounded-md border border-white/10 bg-transparent cursor-pointer"
              />
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  label="To"
                  value={to}
                  onValueChange={(v) => setTo(normalizeHex(v))}
                  variant="bordered"
                  className="text-white"
                  isInvalid={!!to && !isHexColor(to)}
                  errorMessage={!isHexColor(to) ? "Enter a valid hex color" : undefined}
                />
              </div>
              <input
                aria-label="To color"
                type="color"
                value={isHexColor(to) ? to : "#0EA5E9"}
                onChange={(e) => setTo(e.target.value)}
                className="w-11 h-11 rounded-md border border-white/10 bg-transparent cursor-pointer"
              />
            </div>
          </div>

          {/* Angle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-300">Angle</span>
              <span className="text-sm tabular-nums text-zinc-400">{angle}°</span>
            </div>
            <Slider
              aria-label="Angle"
              minValue={0}
              maxValue={360}
              step={1}
              value={angle}
              onChange={setAngle}
              className="max-w-full"
            />
          </div>

          {/* Presets */}
          <div>
            <div className="text-sm text-zinc-300 mb-2">Presets</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {PRESETS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => applyPreset(p)}
                  className="h-10 rounded-md ring-1 ring-white/10 hover:ring-indigo-400 transition"
                  style={{ background: `linear-gradient(90deg, ${p[0]}, ${p[1]})` }}
                  aria-label={`Preset ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Cancel
          </Button>
          <Button color="primary" isDisabled={!canSave} onPress={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
